/** @format */

import React, { useEffect } from "react";
import { useGetResourceSubLessonCardsQuery } from "../../app/services/resource/cardsApi";
import { Card, Sublesson } from "../resources/types";
import AddEdit from "../contentEditor/addEdit";
import Activity from "../activityCards/Activity";
import { UuidString } from "../userResources/userResourceFunctions";
import { useLocale } from "../i18n/hooks";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAppSelector } from "../../app/hooks";
import { selectIsEditable } from "../editable/editableSlice";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SCROLL_TO_QUERY_PARAM_ID } from "../activityCards/CardSlice";

type Props = {
  resourceId?: string;
  subLessonUuid?: UuidString;
  pageId: string;
  sublesson?: Sublesson;
};

const ResourceCards: React.FC<Props> = ({
  resourceId,
  subLessonUuid,
  pageId,
  sublesson,
}) => {
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const isEditable = useAppSelector(selectIsEditable());
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: cards, isFetching } = useGetResourceSubLessonCardsQuery(
    resourceId && subLessonUuid ? { resourceId, subLessonUuid } : skipToken,
  );

  const cardOrder = [...(sublesson?.cards ?? [])].map((item) => item.uuid);

  let _cards = [...(cards ?? [])].filter((card: Card) =>
    cardOrder.includes(card.UUID),
  );

  // Delete the scroll to query param if the card is not in this sub-lesson.
  useEffect(() => {
    const scrollId = searchParams.get(SCROLL_TO_QUERY_PARAM_ID);
    if (isFetching || scrollId === null) return;

    if (_cards.findIndex((card) => card.UUID === scrollId) === -1) {
      searchParams.delete(SCROLL_TO_QUERY_PARAM_ID);
      setSearchParams(searchParams);
    }
  }, [_cards, searchParams, isFetching, setSearchParams]);

  return (
    <>
      {isFetching ? (
        <div
          role="status"
          className="placeholder-card-container w-100 mx-auto d-md-flex mb-4"
          style={{ maxWidth: "1140px" }}
        >
          <div className="placeholder-card bg-white border rounded d-flex flex-column flex-md-row placeholder-glow">
            <div
              className="placeholder-card-margin d-flex justify-content-center flex-shrink-0 placeholder"
              style={{ minHeight: "50px" }}
            ></div>
            <div className="placeholder-card-body px-4 py-5 flex-grow-1 activity-content position-relative placeholder-glow">
              <div
                className={`col-${Math.floor(
                  Math.random() * (6 - 3) + 3,
                )} placeholder d-block mb-5 py-3`}
              ></div>
              {[1, 2, 3].map((index: number) => (
                <div
                  key={`placeholder-line-${index}`}
                  className={`col-${Math.floor(
                    Math.random() * (9 - 3) + 3,
                  )} placeholder d-block my-3`}
                ></div>
              ))}
            </div>
          </div>
          <div
            className={`placeholder-card-tabs d-flex flex-md-column justify-content-end flex-shrink-0 py-md-2 px-2 px-md-0`}
          ></div>
        </div>
      ) : _cards && _cards.length > 0 ? (
        [..._cards]
          .sort((a, b) => cardOrder.indexOf(a.UUID) - cardOrder.indexOf(b.UUID))
          .map((card: Card, index: number) => (
            <div
              key={`${index}-${locale}`}
              data-testid={`${card.UUID}-fullCard`}
            >
              <Activity id={`${pageId}.${index + 1}`} card={card} />
              <AddEdit
                action="card"
                className="maxWidth"
                itemId={`${index + 1}`}
              />
            </div>
          ))
      ) : (
        <div className="no-items-message d-flex w-100 justify-content-center flex-column text-center py-5">
          <h2>{_("This sub-lesson currently has no cards")}</h2>
          {isEditable && (
            <p>{_("To add a card press the '+' button above.")}</p>
          )}
        </div>
      )}
    </>
  );
};

export default ResourceCards;
